import React, { Suspense, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { createCustomTheme } from "./theme";
import { THEMES } from "./theme/constants";
import useSettings from "./hooks/useSettings";
import { ThemeProvider, CssBaseline, Dialog, Box } from "@mui/material";

/* COMPONENTS */
import Loading from "./components/loaders/Loading";
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

//UTILS
import { getFieldData } from "./utils/utils";
import rtlCache from './utils/rltCache';


// ACTIONS
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import OfflinePage from './pages/error/OfflinePage'

//STYLES
import '/node_modules/react-grid-layout/css/styles.css'
import '/node_modules/react-resizable/css/styles.css'

import { useSelector } from "react-redux";
import NavigationHandler from "./utils/NavigateTo";
import StockLayout from "./layouts/StockLayout";
import { hasBootstrappedBefore, markBootstrapped } from "./utils/appBootFlag";

DatePicker.defaultProps = {
  inputFormat: 'DD/MM/YYYY',
};

const reformat = (values) => {
  let final = []
  values?.map((page) => {
    final.push({ name: page.name, route: page.route, isActive: page.isActive })
    if (page.subPage?.length > 0)
      final.push(...reformat(page.subPage))
  })
  return final
}

// rtl-ltr
const ltrCache = createCache({ key: 'mui-ltr' });

const getDirection = (supportedLanguages, language) => {
  const index = supportedLanguages.find(lang => lang.name.toLowerCase() === language.toLowerCase());
  return index ? index.direction : "ltr"; // Default ltr if not found
};

// Convert themePreference ('light', 'dark', 'system') to THEMES.DARK or THEMES.LIGHT
const getEffectiveTheme = (themePreference) => {
  if (themePreference === 'dark') {
    return THEMES.DARK;
  }
  if (themePreference === 'system') {
    // Check browser preference
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? THEMES.DARK : THEMES.LIGHT;
  }
  // Default to light
  return THEMES.LIGHT;
};

export const App = ({
  permissions,
  language,
  loading,
  accessToken,
  themePreference
}) => {
  const supportedLanguages = useSelector((state) => state.translation.supportedLanguages);

  // Captured once at mount: whether this tab already bootstrapped in an earlier
  // page load, so a refresh doesn't flash the full-screen gate over an app that's
  // just silently refetching data it already displayed.
  const [silentReload] = useState(hasBootstrappedBefore);

  const [direction, setDirection] = useState("ltr");
  const location = useLocation();
  
  // Get effective theme based on themePreference and browser preference
  const [effectiveTheme, setEffectiveTheme] = useState(() => 
    getEffectiveTheme(themePreference || 'system')
  );

  useEffect(() => {
    let dir = getDirection(supportedLanguages, language) || 'ltr';
    setDirection(dir);

    if (dir === "rtl") {

      document.documentElement.setAttribute("dir", "rtl");
    } else {

      document.documentElement.setAttribute("dir", "ltr");
    }
  }, [language, supportedLanguages]);

  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const updateOnlineStatus = async () => {
    const onlineStatus = await checkInternetConnection();
    setIsOnline(onlineStatus);
  };

  const checkInternetConnection = async () => {
    let navOn = navigator.onLine
    let googleOn

    try {
      const response = await fetch('https://www.google.com', {
        method: 'HEAD',
        mode: 'no-cors'
      });
      if (response.ok)
        googleOn = true
    } catch (error) {
      googleOn = false;
    }
    return googleOn || navOn
  };

  useEffect(() => {
    updateOnlineStatus();

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    const intervalId = setInterval(() => {
      updateOnlineStatus();
    }, 300000); // 300000 ms = 5 minutes

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
      clearInterval(intervalId);
    };
  }, []);

  // Update effectiveTheme when themePreference changes
  useEffect(() => {
    setEffectiveTheme(getEffectiveTheme(themePreference || 'system'));
  }, [themePreference]);

  // Listen to browser preference changes when themePreference is 'system'
  useEffect(() => {
    if (themePreference === 'system' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        setEffectiveTheme(e.matches ? THEMES.DARK : THEMES.LIGHT);
      };
      
      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      }
      // Fallback for older browsers
      else if (mediaQuery.addListener) {
        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
      }
    }
  }, [themePreference]);

  // Memoized so the title effect below runs on navigation only, not on every shared-store dispatch.
  const allPages = useMemo(
    () => reformat(permissions).filter((data) => data.route && data.isActive),
    [permissions]
  )
  const { settings } = useSettings();
  const theme = createCustomTheme({
    direction: direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    roundedCorners: settings.roundedCorners,
    theme: effectiveTheme,
  });

  // Determine if theme is dark
  // const isDarkMode = theme.palette.mode === 'dark';

  useEffect(() => {
    let selectedPage = allPages.find((page) => page.route === location.pathname)
    document.title = selectedPage ? getFieldData(selectedPage, 'name', language) : 'Resto Admin'
  }, [location.pathname, allPages, language]);

  useEffect(() => {
    if (!loading) markBootstrapped();
  }, [loading]);

  if (loading && !silentReload) {
    return <Loading fullScreen />
  }
  //else
  return (
    <CacheProvider value={direction === 'rtl' ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
          <NavigationHandler />
          <Dialog open={!isOnline} fullScreen>
            <OfflinePage />
          </Dialog>
          <Suspense fallback={<Loading />}>
            <StockLayout/>
          </Suspense>
      </ThemeProvider>
    </CacheProvider>
  );
};


// Redux
const mapStateToProps = (state) => ({
  selectedRestaurant: state.rest.selectedRest,
  selectedBranch: state.branch.selectedBranch,
  branches: state.branch.branches,
  permissions: state.auth.permissions,
  language: state.translation.language,
  loading: state.translation.loading || state.rest.loading || state.translation.supportedLanguagesLoading,
  restaurants: state.rest.restaurants,
  accessToken: state.auth.accessToken,
  themePreference: state.layout?.theme || 'light'
});

const mapDispatchToProps = (dispatch) => ({

});

export default connect(mapStateToProps, mapDispatchToProps)(App);
